import { format, isToday } from 'date-fns';
import { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { ScrollView as GestureHandlerScrollView } from 'react-native-gesture-handler';

import { IconCheck, InlineAlert, Text, ScrollView as UIScrollView } from '@fhs/ui';
import { PICKUP_TIME_SELECTOR_TIME_PICKER } from '@fhs-legacy/frontend/src/utils/test-ids';

// GestureHandlerScrollView on native to work properly inside ActionSheet on Android
const ScrollView = (Platform.OS === 'web' ? UIScrollView : GestureHandlerScrollView) as any;

export default function TimePicker({
  datePickerData,
  selectedDay,
  selectedSlot,
  setSelectedSlot,
  isRestaurantOpen,
  warningMessage = '',
}) {
  const times = datePickerData[selectedDay]?.options ?? [];

  // Find the first available time slot from the first available day
  const firstAvailableTimeSlot = useMemo(() => {
    if (!datePickerData || datePickerData.length === 0) {
      return null;
    }

    // Find the first available day
    const firstAvailableDay = datePickerData.find(
      day => !day.isUnavailable && day.options.length > 0
    );

    if (!firstAvailableDay?.options?.[0]) {
      return null;
    }

    return firstAvailableDay.options[0].time;
  }, [datePickerData]);

  return (
    <View testID={PICKUP_TIME_SELECTOR_TIME_PICKER} style={styles.container}>
      <Text.Heading type="three" style={styles.heading}>
        Select Time
      </Text.Heading>

      {warningMessage ? (
        <View style={styles.warningContainer}>
          <InlineAlert type="warning" message={warningMessage} />
        </View>
      ) : null}

      {times.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text.Ui size="lg" style={styles.emptyText}>
            No available timeslots
          </Text.Ui>
        </View>
      ) : (
        <ScrollView style={styles.timeslotContainer}>
          {times.map(({ time }) => {
            // Check if this time matches the first available time slot today
            // Use tolerance-based comparison (within 1 second) to handle any timing differences
            const isAsapTime =
              firstAvailableTimeSlot && isToday(firstAvailableTimeSlot) && isRestaurantOpen
                ? Math.abs(time.getTime() - firstAvailableTimeSlot.getTime()) < 1000
                : false;

            return (
              <Pressable
                key={time}
                style={styles.timeslotRow}
                onPress={() => setSelectedSlot(time.toISOString())}
              >
                {selectedSlot === time.toISOString() && <IconCheck size={18} />}
                <Text.Ui size="lg">{isAsapTime ? 'ASAP' : format(time, 'h:mm a')}</Text.Ui>{' '}
              </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 28,
  },
  heading: {
    marginTop: 24,
    marginBottom: 8,
  },
  warningContainer: {
    marginBottom: 8,
  },
  timeslotContainer: {
    height: 224,
  },
  timeslotRow: {
    flexDirection: 'row',
    gap: 8,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 6,
  },
  emptyContainer: {
    height: 224,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    color: 'rgba(0, 0, 0, 0.5)',
  },
});
